import { useState, useMemo } from 'react'
import { X, Search, Calendar, Clock } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'
import { useAuth } from '@/lib/auth'
import { useCreateAppearance, useEpisodeAppearances } from '@/hooks/useAppearances'
import { EmailPromptStep } from './EmailPromptStep'

interface GuestData {
  id: string
  name: string
  title: string
  company: string | null
  avatar_url: string | null
  email: string | null
}

interface AddGuestToEpisodeModalProps {
  isOpen: boolean
  onClose: () => void
  episodeId: string
  episodeTitle: string
  currentRecordingDate?: string
  currentRecordingTime?: string
  currentMeetingLink?: string
  onSuccess?: () => void
}

export function AddGuestToEpisodeModal({
  isOpen,
  onClose,
  episodeId,
  episodeTitle,
  currentRecordingDate,
  currentRecordingTime,
  currentMeetingLink,
  onSuccess,
}: AddGuestToEpisodeModalProps) {
  // Generate a key to force form reset when modal opens
  const modalKey = isOpen ? `${episodeId}-open` : 'closed'

  if (!isOpen) return null

  return (
    <AddGuestToEpisodeModalContent
      key={modalKey}
      onClose={onClose}
      episodeId={episodeId}
      episodeTitle={episodeTitle}
      currentRecordingDate={currentRecordingDate}
      currentRecordingTime={currentRecordingTime}
      currentMeetingLink={currentMeetingLink}
      onSuccess={onSuccess}
    />
  )
}

interface AddGuestToEpisodeModalContentProps {
  onClose: () => void
  episodeId: string
  episodeTitle: string
  currentRecordingDate?: string
  currentRecordingTime?: string
  currentMeetingLink?: string
  onSuccess?: () => void
}

interface CreatedAppearanceData {
  id: string
  guest: {
    id: string
    name: string
    email: string | null
  }
}

function AddGuestToEpisodeModalContent({
  onClose,
  episodeId,
  episodeTitle,
  currentRecordingDate,
  currentRecordingTime,
  currentMeetingLink,
  onSuccess,
}: AddGuestToEpisodeModalContentProps) {
  const { membership } = useAuth()
  const supabase = createClient()
  const queryClient = useQueryClient()

  const [step, setStep] = useState<'add' | 'email-prompt'>('add')
  const [createdAppearance, setCreatedAppearance] = useState<CreatedAppearanceData | null>(null)
  const [mode, setMode] = useState<'select' | 'create'>('select')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedGuest, setSelectedGuest] = useState<GuestData | null>(null)
  const [recordingDate, setRecordingDate] = useState(
    currentRecordingDate || new Date().toISOString().split('T')[0]
  )
  const [recordingTime, setRecordingTime] = useState(currentRecordingTime || '')
  const [note, setNote] = useState('')

  // New guest form fields
  const [newGuestName, setNewGuestName] = useState('')
  const [newGuestTitle, setNewGuestTitle] = useState('')
  const [newGuestCompany, setNewGuestCompany] = useState('')
  const [newGuestEmail, setNewGuestEmail] = useState('')

  // Fetch existing guests for the team
  const { data: allGuests = [] } = useQuery({
    queryKey: ['guests', membership?.team_id],
    queryFn: async () => {
      if (!membership?.team_id) return []
      const { data } = await supabase
        .from('guests')
        .select('id, name, title, company, avatar_url, email')
        .eq('team_id', membership.team_id)
        .eq('status', 'active')
        .order('name')
      return (data as GuestData[]) || []
    },
    enabled: !!membership?.team_id,
  })

  // Fetch existing appearances for this episode to exclude already-added guests
  const { data: existingAppearances = [] } = useEpisodeAppearances(episodeId)
  const existingGuestIds = useMemo(
    () => new Set(existingAppearances.map((a) => a.guest_id)),
    [existingAppearances]
  )

  // Filter guests based on search and exclude already-added guests
  const filteredGuests = useMemo(() => {
    return allGuests.filter((guest) => {
      if (existingGuestIds.has(guest.id)) return false
      if (!searchQuery) return true
      const query = searchQuery.toLowerCase()
      return (
        guest.name.toLowerCase().includes(query) ||
        guest.title.toLowerCase().includes(query) ||
        guest.company?.toLowerCase().includes(query) ||
        guest.email?.toLowerCase().includes(query)
      )
    })
  }, [allGuests, searchQuery, existingGuestIds])

  // Create appearance mutation
  const createAppearance = useCreateAppearance()

  // Create guest mutation
  const createGuest = useMutation({
    mutationFn: async () => {
      if (!membership?.team_id) throw new Error('No team')
      const { data, error } = await supabase
        .from('guests')
        .insert({
          team_id: membership.team_id,
          name: newGuestName.trim(),
          title: newGuestTitle.trim(),
          company: newGuestCompany.trim() || null,
          email: newGuestEmail.trim() || null,
          status: 'active',
        } as never)
        .select()
        .single()

      if (error) throw error
      return data as GuestData
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] })
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      let guestId: string
      let guestName: string
      let guestEmail: string | null

      if (mode === 'create') {
        // Create the new guest first
        const newGuest = await createGuest.mutateAsync()
        guestId = newGuest.id
        guestName = newGuestName.trim()
        guestEmail = newGuestEmail.trim() || null
      } else {
        if (!selectedGuest) return
        guestId = selectedGuest.id
        guestName = selectedGuest.name
        guestEmail = selectedGuest.email
      }

      // Create the appearance
      const appearance = await createAppearance.mutateAsync({
        guest_id: guestId,
        episode_id: episodeId,
        recording_date: recordingDate,
        recording_time: recordingTime || undefined,
        note: note.trim() || undefined,
      })

      // Store the created appearance data and transition to email prompt step
      setCreatedAppearance({
        id: appearance.id,
        guest: {
          id: guestId,
          name: guestName,
          email: guestEmail,
        },
      })
      setStep('email-prompt')
      onSuccess?.()
    } catch (error) {
      console.error('Failed to add guest to episode:', error)
    }
  }

  const isSubmitting = createAppearance.isPending || createGuest.isPending
  const canSubmit =
    mode === 'select'
      ? !!selectedGuest && !!recordingDate
      : !!newGuestName.trim() && !!newGuestTitle.trim() && !!recordingDate

  // Render email prompt step
  if (step === 'email-prompt' && createdAppearance) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div
          className="relative w-full max-w-lg mx-4 bg-white dark:bg-zinc-900 rounded-xl shadow-xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <EmailPromptStep
            appearance={{ id: createdAppearance.id }}
            guest={createdAppearance.guest}
            episode={{
              id: episodeId,
              title: episodeTitle,
              recording_date: recordingDate,
              recording_time: recordingTime || null,
              meeting_link: currentMeetingLink || null,
            }}
            onClose={onClose}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="add-guest-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} data-testid="add-guest-modal-backdrop" />

      {/* Modal */}
      <div
        className="relative w-full max-w-lg mx-4 bg-white dark:bg-zinc-900 rounded-xl shadow-xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-800 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              Add Guest to Episode
            </h2>
            <p className="text-sm text-zinc-500 dark:text-zinc-400" data-testid="add-guest-episode-title">{episodeTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            data-testid="add-guest-modal-close"
          >
            <X className="w-5 h-5 text-zinc-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
            {/* Mode toggle */}
            <div className="flex rounded-lg bg-zinc-100 dark:bg-zinc-800 p-1" data-testid="add-guest-mode-toggle">
              <button
                type="button"
                onClick={() => setMode('select')}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  mode === 'select'
                    ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 shadow-sm'
                    : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200'
                }`}
                data-testid="add-guest-mode-select"
              >
                Select Existing
              </button>
              <button
                type="button"
                onClick={() => setMode('create')}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  mode === 'create'
                    ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 shadow-sm'
                    : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200'
                }`}
                data-testid="add-guest-mode-create"
              >
                Create New
              </button>
            </div>

            {mode === 'select' ? (
              <>
                {/* Guest search */}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    Select Guest <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search guests..."
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      data-testid="add-guest-search"
                    />
                  </div>

                  {/* Guest list */}
                  <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-700" data-testid="add-guest-list">
                    {filteredGuests.length === 0 ? (
                      <div className="p-4 text-center text-sm text-zinc-500 dark:text-zinc-400" data-testid="add-guest-list-empty">
                        {allGuests.length === 0
                          ? 'No guests yet. Create one to get started.'
                          : searchQuery
                          ? 'No guests match your search.'
                          : 'All guests are already added to this episode.'}
                      </div>
                    ) : (
                      <div className="divide-y divide-zinc-200 dark:divide-zinc-700">
                        {filteredGuests.map((guest) => (
                          <button
                            key={guest.id}
                            type="button"
                            onClick={() => setSelectedGuest(guest)}
                            className={`w-full flex items-center gap-3 p-3 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors ${
                              selectedGuest?.id === guest.id
                                ? 'bg-primary-50 dark:bg-primary-900/20 border-l-2 border-primary-500'
                                : ''
                            }`}
                            data-testid={`add-guest-item-${guest.id}`}
                          >
                            <div className="w-10 h-10 rounded-full bg-secondary-500 flex items-center justify-center text-white font-medium flex-shrink-0">
                              {guest.avatar_url ? (
                                <img
                                  src={guest.avatar_url}
                                  alt={guest.name}
                                  className="w-full h-full rounded-full object-cover"
                                />
                              ) : (
                                guest.name.charAt(0)
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-zinc-900 dark:text-zinc-100 truncate">
                                {guest.name}
                              </p>
                              <p className="text-sm text-zinc-500 dark:text-zinc-400 truncate">
                                {guest.title}
                                {guest.company && ` at ${guest.company}`}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div data-testid="add-guest-create-form">
                {/* New guest form */}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newGuestName}
                    onChange={(e) => setNewGuestName(e.target.value)}
                    placeholder="Guest name"
                    className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    data-testid="add-guest-name-input"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    Title <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newGuestTitle}
                    onChange={(e) => setNewGuestTitle(e.target.value)}
                    placeholder="e.g., CEO, Author, Developer"
                    className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    data-testid="add-guest-title-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                      Company
                    </label>
                    <input
                      type="text"
                      value={newGuestCompany}
                      onChange={(e) => setNewGuestCompany(e.target.value)}
                      placeholder="Company name"
                      className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      data-testid="add-guest-company-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={newGuestEmail}
                      onChange={(e) => setNewGuestEmail(e.target.value)}
                      placeholder="guest@email.com"
                      className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      data-testid="add-guest-email-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-zinc-200 dark:border-zinc-700 pt-4">
              <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-3">
                Recording Schedule
              </p>
            </div>

            {/* Recording date and time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Recording Date <span className="text-rose-500">*</span>
                  </span>
                </label>
                <input
                  type="date"
                  value={recordingDate}
                  onChange={(e) => setRecordingDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                  data-testid="add-guest-recording-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Recording Time
                  </span>
                </label>
                <input
                  type="time"
                  value={recordingTime}
                  onChange={(e) => setRecordingTime(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  data-testid="add-guest-recording-time"
                />
              </div>
            </div>

            {/* Note */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                Note (optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Any notes about this appearance..."
                rows={2}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                data-testid="add-guest-note-input"
              />
            </div>

            {/* Error display */}
            {(createAppearance.isError || createGuest.isError) && (
              <p className="text-sm text-rose-600 dark:text-rose-400" data-testid="add-guest-error">
                Failed to add guest. Please try again.
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-zinc-200 dark:border-zinc-800 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-50"
              data-testid="add-guest-cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="add-guest-submit-button"
            >
              {isSubmitting ? 'Adding...' : 'Add Guest'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
